import React, { useEffect, useRef, useState } from 'react';
import { Box, Alert, CircularProgress } from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import TextInput from '../Inputs/TextInput';
import Subtitle from '../Texts/Subtitle';
import OutlineButton from '../Buttons/OutlineButton';
import MainLogo from '../Titles/MainLogo';
import ConnectFormContainer from '../Containers/ConnectFormContainer';
import SmallLink from '../Links/SmallLink';
import { getApiUrl } from '../../config/api';
import { translateApiError } from '../../utils/translateApiError';

const MIN_PASSWORD_LENGTH = 6;

const ResetPasswordForm: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const resetToken = new URLSearchParams(location.search).get('token');

  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const [error, setError] = useState<string>('');
  const [confirmError, setConfirmError] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [tokenValidating, setTokenValidating] = useState(Boolean(resetToken));
  const [tokenValid, setTokenValid] = useState(false);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (!resetToken) return;

    let cancelled = false;
    (async () => {
      setTokenValidating(true);
      setError('');
      try {
        const apiUrl = getApiUrl('/api/v1/auth/reset/validate');
        await axios.get(apiUrl, {
          params: { token: resetToken },
          withCredentials: true,
        });
        if (!cancelled) {
          setTokenValid(true);
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const response = (err as { response?: { data?: { errors?: Array<{ msg?: string }>; message?: string; code?: string }; status?: number } })
          ?.response;
        const data = response?.data;
        setTokenValid(false);
        setError(
          translateApiError(t, {
            message: data?.errors?.[0]?.msg || data?.message,
            code: data?.code,
            status: response?.status,
          }, 'auth.resetPasswordTokenInvalid'),
        );
      } finally {
        if (!cancelled) {
          setTokenValidating(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resetToken, t]);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const apiUrl = getApiUrl('/api/v1/auth/recover');
      await axios.post(apiUrl, { email }, { withCredentials: true });
      setRequestSubmitted(true);
    } catch (err: unknown) {
      const response = (err as { response?: { data?: { message?: string; code?: string }; status?: number } })
        ?.response;
      setError(
        translateApiError(t, {
          message: response?.data?.message,
          code: response?.data?.code,
          status: response?.status,
        }, 'auth.resetPasswordFailed'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setConfirmError('');

    if (isSubmittingRef.current || submitting) return;

    const token = new URLSearchParams(location.search).get('token')?.trim();
    if (!token) {
      setError(t('auth.resetPasswordTokenInvalid'));
      return;
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t('auth.resetPasswordTooShort'));
      return;
    }
    if (password !== confirmPassword) {
      setConfirmError(t('auth.resetPasswordMismatch'));
      return;
    }

    isSubmittingRef.current = true;
    setSubmitting(true);
    try {
      const apiUrl = getApiUrl('/api/v1/auth/reset');
      await axios.post(
        apiUrl,
        { resetPasswordToken: token, password },
        { withCredentials: true },
      );
      setPasswordUpdated(true);
    } catch (err: unknown) {
      const response = (err as { response?: { data?: { errors?: Array<{ msg?: string }>; message?: string; code?: string }; status?: number } })
        ?.response;
      const data = response?.data;
      setError(
        translateApiError(t, {
          message: data?.errors?.[0]?.msg || data?.message,
          code: data?.code,
          status: response?.status,
        }, 'auth.resetPasswordTokenInvalid'),
      );
    } finally {
      isSubmittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (resetToken) {
    return (
      <ConnectFormContainer>
        <MainLogo />
        <Subtitle>{t('auth.resetPasswordNewTitle')}</Subtitle>

        {tokenValidating ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress size={32} />
          </Box>
        ) : null}

        {passwordUpdated ? (
          <Alert severity="success" sx={{ margin: '1.5em auto' }}>{t('auth.resetPasswordComplete')}</Alert>
        ) : null}
        {error ? <Alert severity="error">{error}</Alert> : null}

        {!passwordUpdated && tokenValid && !tokenValidating ? (
          <form onSubmit={handleSetNewPassword} style={{ width: '100%' }} autoComplete="on">
            <TextInput
              label={t('auth.newPassword')}
              name="password"
              type="password"
              autoComplete="new-password"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => {
                const next = (e.target as HTMLInputElement).value;
                setPassword(next);
                if (confirmPassword && next !== confirmPassword) {
                  setConfirmError(t('auth.resetPasswordMismatch'));
                } else {
                  setConfirmError('');
                }
              }}
              required
              showPasswordToggle
              showPasswordLabel={t('auth.showPassword')}
              hidePasswordLabel={t('auth.hidePassword')}
            />
            <TextInput
              label={t('auth.confirmNewPassword')}
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              fullWidth
              margin="normal"
              value={confirmPassword}
              onChange={(e) => {
                const next = (e.target as HTMLInputElement).value;
                setConfirmPassword(next);
                if (password && next && password !== next) {
                  setConfirmError(t('auth.resetPasswordMismatch'));
                } else {
                  setConfirmError('');
                }
              }}
              required
              error={Boolean(confirmError)}
              helperText={confirmError || undefined}
              showPasswordToggle
              showPasswordLabel={t('auth.showPassword')}
              hidePasswordLabel={t('auth.hidePassword')}
            />
            <OutlineButton type="submit" fullWidth disabled={submitting || Boolean(confirmError)}>
              {t('auth.resetPasswordSave')}
            </OutlineButton>
          </form>
        ) : null}

        <Box textAlign="center" display="flex" flexDirection="column" alignItems="center">
          <SmallLink to="/">{t('auth.login')}</SmallLink>
        </Box>
      </ConnectFormContainer>
    );
  }

  return (
    <ConnectFormContainer>
      <MainLogo />
      <Subtitle>{t('auth.forgotPassword')}</Subtitle>

      {requestSubmitted ? (
        <Alert severity="success" sx={{ margin: '1.5em auto' }}>{t('auth.resetPasswordSuccess')}</Alert>
      ) : null}
      {error ? <Alert severity="error">{error}</Alert> : null}

      {!requestSubmitted ? (
        <form onSubmit={handleRequestReset} style={{ width: '100%' }} autoComplete="on">
          <TextInput
            label={t('auth.email')}
            name="email"
            type="email"
            autoComplete="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
            required
          />
          <OutlineButton type="submit" fullWidth disabled={submitting}>
            {t('auth.resetPasswordSubmit')}
          </OutlineButton>
        </form>
      ) : null}

      <Box textAlign="center" display="flex" flexDirection="column" alignItems="center">
        <SmallLink to="/register">{t('auth.register')}</SmallLink>
        <SmallLink to="/">{t('auth.login')}</SmallLink>
      </Box>
    </ConnectFormContainer>
  );
};

export default ResetPasswordForm;
